import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Price from '@/components/ui/Price';
import type { OrderListEntry } from '@/types/account';
import { useGetMyOrdersQuery, transformConsumerListEntry } from '@/features/checkout/ordersApi';
import CancellationPolicyNotice from '@/features/restaurant/CancellationPolicyNotice';
import { useGetRestaurantIdentityQuery } from '@/app/api/hubApi';
import { restaurantTz, todayIsoInTz } from '@/lib/hours';

type StatusChip = 'all' | 'cancelled';
type DateChip = 'all' | '30d' | '3m' | '12m';

const STATUS_CHIPS: { id: StatusChip; labelKey: string }[] = [
  { id: 'all', labelKey: 'account.orders.filters.all' },
  { id: 'cancelled', labelKey: 'account.orders.filters.cancelled' },
];

const DATE_CHIPS: { id: DateChip; labelKey: string }[] = [
  { id: 'all', labelKey: 'account.orders.filters.all' },
  { id: '30d', labelKey: 'orders.date30' },
  { id: '3m',  labelKey: 'orders.date3m' },
  { id: '12m', labelKey: 'orders.date12m' },
];

function statusToBackend(chip: StatusChip): string | undefined {
  switch (chip) {
    case 'cancelled': return 'cancelled';
    default:          return undefined;
  }
}

// SC-1081 — jour civil du restaurant, pas le jour UTC.
function dateToSince(chip: DateChip, tz: string): string | undefined {
  if (chip === 'all') return undefined;
  const days = chip === '30d' ? 30 : chip === '3m' ? 90 : 365;
  return todayIsoInTz(tz, new Date(Date.now() - days * 24 * 60 * 60 * 1000));
}

export default function OrdersPage() {
  const { t } = useTranslation();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const [statusChip, setStatusChip] = useState<StatusChip>('all');
  const [dateChip, setDateChip] = useState<DateChip>('all');
  const [page, setPage] = useState(1);
  const { data: identity } = useGetRestaurantIdentityQuery(restaurantUuid ?? '', { skip: !restaurantUuid });
  const tz = restaurantTz(identity ?? {});
  const [accumulated, setAccumulated] = useState<OrderListEntry[]>([]);

  useEffect(() => {
    setPage(1);
    setAccumulated([]);
  }, [statusChip, dateChip]);

  const queryArgs = {
    restaurantUuid: restaurantUuid ?? '',
    status: statusToBackend(statusChip),
    since: dateToSince(dateChip, tz),
    page,
    pageSize: 10,
  };

  const { data, isFetching, isError, refetch } = useGetMyOrdersQuery(queryArgs);

  useEffect(() => {
    if (data?.results) {
      setAccumulated(prev =>
        page === 1
          ? data.results.map(transformConsumerListEntry)
          : [...prev, ...data.results.map(transformConsumerListEntry)],
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const hasMore = !!data?.next;
  const isFirstFetch = page === 1 && isFetching && accumulated.length === 0;

  const chipClass = (active: boolean) =>
    `px-3 py-1 border-2 border-ink rounded-btn-sm font-display text-sm ${active ? 'bg-ink text-paper' : 'bg-paper text-ink'}`;

  return (
    <div className="space-y-4">
      <CancellationPolicyNotice restaurantUuid={restaurantUuid ?? ''} />

      {/* Status chips */}
      <div className="space-y-1">
        <p className="text-xs text-ink/60 font-display">{t('orders.statusLabel')}</p>
        <div className="flex flex-wrap gap-2">
          {STATUS_CHIPS.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => setStatusChip(c.id)}
              className={chipClass(statusChip === c.id)}
            >
              {t(c.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Date chips */}
      <div className="space-y-1">
        <p className="text-xs text-ink/60 font-display">{t('orders.periodLabel')}</p>
        <div className="flex flex-wrap gap-2">
          {DATE_CHIPS.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => setDateChip(c.id)}
              className={chipClass(dateChip === c.id)}
            >
              {t(c.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {isError && (
        <div className="bg-paper border-2 border-ink p-4 rounded-btn text-center space-y-2">
          <p className="text-ink/70 text-sm">{t('orders.loadError')}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-3 py-1 border-2 border-ink rounded-btn-sm font-display text-sm bg-paper text-ink hover:bg-ink hover:text-paper transition-colors"
          >
            {t('reservation.retry')}
          </button>
        </div>
      )}

      {/* Loading skeleton for first fetch */}
      {isFirstFetch && (
        <ul className="space-y-2" data-testid="orders-skeleton">
          {[0, 1, 2].map(i => (
            <li key={i} className="bg-paper border-2 border-ink p-4 rounded-btn animate-pulse">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-20 bg-ink/10 rounded" />
                  <div className="space-y-1">
                    <div className="h-4 w-28 bg-ink/10 rounded" />
                    <div className="h-3 w-36 bg-ink/10 rounded" />
                  </div>
                </div>
                <div className="h-4 w-16 bg-ink/10 rounded" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Empty state */}
      {!isFirstFetch && !isError && accumulated.length === 0 && (
        <div className="bg-paper border-2 border-ink p-6 rounded-btn text-center text-ink/70">
          {t('orders.emptyText')}
          <Link to={`/${restaurantUuid}`} className="underline">{t('orders.emptyLink')}</Link>
        </div>
      )}

      {/* Order list */}
      {accumulated.length > 0 && (
        <ul className="space-y-2">
          {accumulated.map(o => (
            <li key={o.id}>
              <Link
                to={`/${restaurantUuid}/account/orders/${o.id}`}
                className="block bg-paper border-2 border-ink p-4 rounded-btn hover:-translate-y-[1px] hover:-translate-x-[1px] hover:shadow-stamp-sm transition-all"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-ink">{formatDate(o.placedAt, tz)}</p>
                    <p className="text-xs text-ink/60">
                      {t(o.mode === 'delivery' ? 'order.modeDelivery' : 'order.modeTakeaway')} · {t('order.itemsCount', { n: o.lineCount })}
                      {o.firstItemName ? ` · ${o.firstItemName}` : ''}
                    </p>
                  </div>
                  <div className="font-display text-ink"><Price amountCents={o.total} /></div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* Charger plus */}
      {hasMore && !isError && (
        <div className="text-center">
          <button
            type="button"
            disabled={isFetching}
            onClick={() => setPage(p => p + 1)}
            className="px-4 py-2 border-2 border-ink rounded-btn font-display text-sm bg-paper text-ink hover:bg-ink hover:text-paper transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetching ? t('loading.label') : t('orders.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string, tz: string) {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz });
}
